import { useMemo } from "react";
import type { CollectionName, HabitHealthMetric } from "@lockcal/shared";
import { useAppStore } from "@/store/useAppStore";
import type { LocalRecord } from "@/data/db";
import { expandOccurrences, type Occurrence } from "@/lib/recurrence";
import { dayKey, startOfDay, endOfDay, MS_DAY } from "@/lib/date";

export function useCollection(collection: CollectionName): LocalRecord[] {
  return useAppStore((s) => s.data[collection] ?? []);
}

export function useLiveRecords<T>(collection: CollectionName): T[] {
  const records = useCollection(collection);
  return useMemo(
    () => records.filter((r) => !r.deletedAt) as unknown as T[],
    [records],
  );
}

export interface EventOccurrence extends Occurrence {
  eventId: string;
  title: string;
  calendarId: string;
  colorArgb: number;
  isAllDay: boolean;
  location: string | null;
  notes: string | null;
  rrule: string | null;
}

/**
 * Expand every event into the concrete occurrences inside the given range.
 */
export function useOccurrences(
  rangeStart: number,
  rangeEnd: number,
): EventOccurrence[] {
  const events = useLiveRecords<
    LocalRecord & {
      title: string;
      calendarId: string;
      startEpochMillis: number;
      endEpochMillis: number;
      isAllDay: boolean;
      rrule: string | null;
      repeatEndEpochMillis: number | null;
      customColorArgb: number | null;
      location: string | null;
      notes: string | null;
      excludedOccurrenceStarts: number[];
    }
  >("events");
  const calendars = useLiveRecords<
    LocalRecord & { title: string; colorArgb: number }
  >("calendars");

  return useMemo(() => {
    const calendarMap = new Map(calendars.map((c) => [c.id, c]));

    const out: EventOccurrence[] = [];
    for (const event of events) {
      const occurrences = expandOccurrences({
        rrule: event.rrule,
        start: event.startEpochMillis,
        end: event.endEpochMillis,
        until: event.repeatEndEpochMillis,
        excluded: event.excludedOccurrenceStarts ?? [],
        rangeStart,
        rangeEnd,
      });
      const calendar = calendarMap.get(event.calendarId);
      for (const occurrence of occurrences) {
        out.push({
          ...occurrence,
          eventId: event.id,
          title: event.title,
          calendarId: event.calendarId,
          colorArgb:
            event.customColorArgb ?? calendar?.colorArgb ?? 0xff6750a4,
          isAllDay: event.isAllDay,
          location: event.location,
          notes: event.notes,
          rrule: event.rrule,
        });
      }
    }
    return out.sort((a, b) => {
      if (a.isAllDay !== b.isAllDay) return a.isAllDay ? -1 : 1;
      return a.start - b.start;
    });
  }, [events, calendars, rangeStart, rangeEnd]);
}

/** Occurrences of a single day. */
export function useDayOccurrences(date: Date | number): EventOccurrence[] {
  const from = startOfDay(date).getTime();
  const to = endOfDay(date).getTime();
  return useOccurrences(from, to + 1);
}

export interface ReminderItem {
  id: string;
  listId: string;
  title: string;
  notes: string | null;
  dueEpochMillis: number | null;
  isCompleted: boolean;
  completedAtEpochMillis: number | null;
  priority: number;
  quadrant: number;
  rrule: string | null;
  repeatEveryDays?: number | null;
  completedOccurrenceStarts?: number[];
  completionHistory?: number[];
}

export function useReminders(): ReminderItem[] {
  const reminders = useLiveRecords<LocalRecord & ReminderItem>("reminders");
  return useMemo(
    () =>
      reminders.sort((a, b) => {
        if (a.isCompleted !== b.isCompleted) return a.isCompleted ? 1 : -1;
        const aDue = a.dueEpochMillis ?? Number.MAX_SAFE_INTEGER;
        const bDue = b.dueEpochMillis ?? Number.MAX_SAFE_INTEGER;
        if (aDue !== bDue) return aDue - bDue;
        return b.priority - a.priority;
      }),
    [reminders],
  );
}

export interface HabitItem {
  id: string;
  title: string;
  icon: string | null;
  color: number;
  type: string;
  target: number;
  unit: string | null;
  healthMetric: HabitHealthMetric | null;
  orderIndex: number;
}

export interface HabitWithProgress extends HabitItem {
  progress: number;
  done: boolean;
}

/** Habits with the progress of the given day. */
export function useHabitsWithProgress(date: Date | number): HabitWithProgress[] {
  const habits = useLiveRecords<LocalRecord & HabitItem>("habits");
  const checkIns = useLiveRecords<
    LocalRecord & { habitId: string; dayKey: string; progress: number }
  >("habitCheckIns");

  return useMemo(() => {
    const key = dayKey(date);
    const byHabit = new Map<string, number>();
    for (const checkIn of checkIns) {
      if (checkIn.dayKey !== key) continue;
      byHabit.set(
        checkIn.habitId,
        (byHabit.get(checkIn.habitId) ?? 0) + checkIn.progress,
      );
    }
    return habits
      .map((habit) => {
        const progress = byHabit.get(habit.id) ?? 0;
        return {
          ...habit,
          progress,
          done: progress >= (habit.target || 1),
        };
      })
      .sort((a, b) => a.orderIndex - b.orderIndex);
  }, [habits, checkIns, date]);
}

/** Streak of consecutive days a habit has been completed. */
export function useHabitStreaks(): Map<string, number> {
  const checkIns = useLiveRecords<
    LocalRecord & { habitId: string; dayKey: string; progress: number }
  >("habitCheckIns");
  const habits = useLiveRecords<LocalRecord & HabitItem>("habits");

  return useMemo(() => {
    const byHabit = new Map<string, Set<string>>();
    for (const checkIn of checkIns) {
      if (!byHabit.has(checkIn.habitId)) byHabit.set(checkIn.habitId, new Set());
      byHabit.get(checkIn.habitId)!.add(checkIn.dayKey);
    }

    const streaks = new Map<string, number>();
    const today = startOfDay(new Date()).getTime();

    for (const habit of habits) {
      const days = byHabit.get(habit.id);
      if (!days || days.size === 0) {
        streaks.set(habit.id, 0);
        continue;
      }
      let streak = 0;
      // Today may not be checked in yet, so start from yesterday in that case.
      let cursor = days.has(dayKey(today)) ? today : today - MS_DAY;
      while (days.has(dayKey(cursor))) {
        streak++;
        cursor -= MS_DAY;
      }
      streaks.set(habit.id, streak);
    }
    return streaks;
  }, [checkIns, habits]);
}

export function useSettings() {
  const settings = useLiveRecords<
    LocalRecord & {
      locale: string;
      themeMode: "system" | "light" | "dark";
      use24HourClock: boolean;
      firstDayOfWeek: number;
      showHabitWidgets: boolean;
    }
  >("settings");
  return settings.find((s) => s.id === "singleton");
}
